import { makeAuthenticatedRequest } from './authService';

// Interfaces based on the backend schedule_events table
export interface ScheduleEvent {
  id: number;
  user_id: number;
  title: string;
  description?: string;
  event_date?: string; // YYYY-MM-DD format, null for repeating events
  event_time: string; // HH:MM:SS format
  duration_minutes: number;
  color: string; // Hex color code
  is_repeating: boolean;
  repeat_type: 'none' | 'daily' | 'weekly' | 'monthly';
  repeat_days?: string; // Comma-separated day numbers (1=Mon, 7=Sun)
  repeat_start_date?: string; // YYYY-MM-DD format
  repeat_end_date?: string; // YYYY-MM-DD format, null for no end
  parent_event_id?: number; // For exception events
  is_exception?: boolean; // True for exception events
  exception_date?: string; // YYYY-MM-DD format for exception events
  is_cancelled?: boolean; // True for cancelled exception events
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateScheduleEventData {
  user_id: number;
  title: string;
  description?: string;
  event_date?: string; // Required for non-repeating events
  event_time: string; // HH:MM format
  duration_minutes: number;
  color?: string; // Defaults to #3498db
  is_repeating?: boolean; // Defaults to false
  repeat_type?: 'none' | 'daily' | 'weekly' | 'monthly'; // Defaults to 'none'
  repeat_days?: string; // Required for weekly/monthly repeating events
  repeat_start_date?: string; // Required for repeating events
  repeat_end_date?: string; // Optional end date for repeating events
}

export interface UpdateScheduleEventData {
  title?: string;
  description?: string;
  event_date?: string;
  event_time?: string;
  duration_minutes?: number;
  color?: string;
  is_repeating?: boolean;
  repeat_type?: 'none' | 'daily' | 'weekly' | 'monthly';
  repeat_days?: string;
  repeat_start_date?: string;
  repeat_end_date?: string;
}

export interface ScheduleFilters {
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
  include_inactive?: boolean;
}

class ScheduleService {
  /**
   * Get schedule events for a specific date
   * GET /api/schedules/user/:userId/date/:date
   */
  async getScheduleEventsByDate(userId: number, date: string): Promise<ScheduleEvent[]> {
    try {
      const response = await makeAuthenticatedRequest(`/schedules/user/${userId}/date/${date}`, {
        method: 'GET'
      });
      
      if (response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching schedule events by date:', error);
      throw error;
    }
  }

  /**
   * Get schedule events for a user within a date range
   * GET /api/schedules/user/:userId
   */
  async getScheduleEvents(userId: number, filters?: ScheduleFilters): Promise<{ events: ScheduleEvent[]; pagination?: any }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.start_date) queryParams.append('start_date', filters.start_date);
      if (filters?.end_date) queryParams.append('end_date', filters.end_date);
      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.include_inactive) queryParams.append('include_inactive', filters.include_inactive.toString());
      
      const queryString = queryParams.toString();
      const url = `/schedules/user/${userId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      if (response.success && response.data) {
        return {
          events: Array.isArray(response.data) ? response.data : [],
          pagination: response.pagination
        };
      }
      return { events: [] };
    } catch (error) {
      console.error('Error fetching schedule events:', error);
      throw error;
    }
  }

  /**
   * Get a schedule event by ID
   * GET /api/schedules/:eventId
   */
  async getScheduleEvent(eventId: number): Promise<ScheduleEvent | null> {
    try {
      const response = await makeAuthenticatedRequest(`/schedules/${eventId}`, {
        method: 'GET'
      });
      
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching schedule event:', error);
      throw error;
    }
  }

  /**
   * Create a new schedule event
   * POST /api/schedules
   */
  async createScheduleEvent(eventData: CreateScheduleEventData): Promise<{ success: boolean; data?: ScheduleEvent; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/schedules', {
        method: 'POST',
        body: JSON.stringify(eventData)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating schedule event:', error);
      throw error;
    }
  }

  /**
   * Update a schedule event
   * PUT /api/schedules/:eventId
   */
  async updateScheduleEvent(eventId: number, updateData: UpdateScheduleEventData): Promise<{ success: boolean; data?: ScheduleEvent; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/schedules/${eventId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating schedule event:', error);
      throw error;
    }
  }

  /**
   * Delete a schedule event (soft delete)
   * DELETE /api/schedules/:eventId
   */
  async deleteScheduleEvent(eventId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/schedules/${eventId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting schedule event:', error);
      throw error;
    }
  }

  /**
   * Delete a specific instance of a repeating event
   * DELETE /api/schedules/:eventId/instance
   */
  async deleteScheduleEventInstance(eventId: number, date: string): Promise<{ success: boolean; message?: string; data?: any }> {
    try {
      const response = await makeAuthenticatedRequest(`/schedules/${eventId}/instance`, {
        method: 'DELETE',
        body: JSON.stringify({ date })
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting schedule event instance:', error);
      throw error;
    }
  }

  /**
   * Get repeating events for a user
   * GET /api/schedules/user/:userId/repeating
   */
  async getRepeatingEvents(userId: number): Promise<ScheduleEvent[]> {
    try {
      const response = await makeAuthenticatedRequest(`/schedules/user/${userId}/repeating`, {
        method: 'GET'
      });
      
      if (response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching repeating events:', error);
      throw error;
    }
  }

  /**
   * Create an exception for a repeating event
   * POST /api/schedules/:eventId/exception
   */
  async createException(eventId: number, exceptionData: {
    exception_date: string;
    title?: string;
    description?: string;
    event_time?: string;
    duration_minutes?: number;
    color?: string;
  }): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/schedules/${eventId}/exception`, {
        method: 'POST',
        body: JSON.stringify(exceptionData)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating exception:', error);
      throw error;
    }
  }

  /**
   * Helper function to determine if a repeating event should occur on a specific date
   */
  shouldEventOccurOnDate(event: ScheduleEvent, dateString: string): boolean {
    if (!event.is_repeating) {
      return false;
    }

    const targetDate = new Date(dateString);
    const startDate = event.repeat_start_date ? new Date(event.repeat_start_date) : null;
    const endDate = event.repeat_end_date ? new Date(event.repeat_end_date) : null;

    // Check if date is within the repeat range
    if (startDate && targetDate < startDate) {
      return false;
    }
    if (endDate && targetDate > endDate) {
      return false;
    }

    // Check repeat pattern
    switch (event.repeat_type) {
      case 'daily':
        return true;
      
      case 'weekly':
        if (!event.repeat_days) {
          return true; // No specific days means every day
        }
        const dayOfWeek = targetDate.getDay() === 0 ? 7 : targetDate.getDay(); // Convert Sunday from 0 to 7
        return event.repeat_days.split(',').includes(dayOfWeek.toString());
      
      case 'monthly':
        // Simple monthly implementation - same day of week in month
        if (!event.repeat_days) {
          return true;
        }
        const monthDayOfWeek = targetDate.getDay() === 0 ? 7 : targetDate.getDay();
        return event.repeat_days.split(',').includes(monthDayOfWeek.toString());
      
      default:
        return false;
    }
  }
}

// Export a singleton instance
export const scheduleService = new ScheduleService();

